/**
 * Module-level singleton for a shared, lazily-created sandboxed iframe.
 *
 * A single hidden <iframe> is appended to document.body on first access and
 * reused by every WorkerWebRuntime instance in the same page. The iframe is
 * intentionally managed imperatively (outside of React) so that the React
 * reconciler can never unmount or reorder it.
 *
 * Usage:
 *   const iframe = getOrCreateSandboxIframe();
 *   destroySandboxIframe();                    // teardown / test cleanup
 */

import type { ProductEnvironment } from '@atlassian/forge-ui-types/environments';

import { getForgeRuntimeSandboxSrc } from './utils';

/**
 * Stable DOM id used as the cross-instance singleton registry.
 */
export const SANDBOX_IFRAME_ID = '__forge-worker-sandbox__';

let iframe: HTMLIFrameElement | null = null;

const createSandboxIframe = (
	document: Document,
	environment: ProductEnvironment,
): HTMLIFrameElement => {
	const iframeEl = document.createElement('iframe');
	iframeEl.id = SANDBOX_IFRAME_ID;

	// technically we could remove 'allow-same-origin' and this will ensure all
	// worker runtime code is completely isolated (not able to read cookies or localStorage)
	// but this could prevent potential future features that require same-origin access,
	// (e.g. service-worker, indexdb etc.), so we'll keep it in place for now and revisit if needed.
	//
	// in order to secure the worker runtime, we will be utilising the security shim
	// script suggested in: https://hello.atlassian.net/wiki/x/dDy-hAE
	iframeEl.setAttribute('sandbox', 'allow-scripts allow-same-origin');
	iframeEl.src = getForgeRuntimeSandboxSrc(environment);

	// Keep the iframe invisible but laid out so the browser doesn't defer script
	// execution inside it. `display:none` can suspend execution in some browsers.
	Object.assign(iframeEl.style, {
		position: 'absolute',
		width: '0',
		height: '0',
		border: 'none',
		top: '-9999px',
		left: '-9999px',
		visibility: 'hidden',
	});
	iframeEl.setAttribute('tabindex', '-1');
	iframeEl.setAttribute('aria-hidden', 'true');
	return iframeEl;
};

/**
 * Returns (and lazily creates) the shared sandbox iframe element.
 * Safe to call multiple times — the same element is always returned.
 *
 * Returns `null` in non-browser environments (SSR).
 */
export const getOrCreateSandboxIframe = (
	environment: ProductEnvironment,
): HTMLIFrameElement | null => {
	if (typeof document === 'undefined') {
		// SSR guard — no DOM available
		return null;
	}

	// Fast path: module-level cache (same module instance, same call).
	if (iframe !== null) {
		return iframe;
	}

	// Recovery path: find an existing iframe created by a different module
	// instance (Module Federation) or a previous module load (HMR).
	const existing = document.getElementById(SANDBOX_IFRAME_ID) as HTMLIFrameElement | null;
	if (existing !== null) {
		iframe = existing;
		return iframe;
	}

	// First-ever creation: build the element and stamp it with the stable id.
	const iframeEl = createSandboxIframe(document, environment);
	document.body.appendChild(iframeEl);
	iframe = iframeEl;

	return iframe;
};

/**
 * Removes the singleton iframe from the DOM and resets the module state.
 *
 * Intended for test teardown and explicit cleanup. After calling this,
 * the next call to `getOrCreateSandboxIframe()` will create a fresh iframe.
 */
export const destroySandboxIframe = (): void => {
	// Query the DOM directly rather than relying solely on the module variable.
	// A different module instance may have been the one to create the iframe,
	// so `iframeEl` could be null here even if the element exists in the DOM.
	const el =
		iframe ??
		(typeof document !== 'undefined'
			? (document.getElementById(SANDBOX_IFRAME_ID) as HTMLIFrameElement | null)
			: null);
	if (el !== null) {
		el.remove();
	}
	iframe = null;
};
